import { AutoGraph } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Button, Snackbar } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Modal from '@mui/material/Modal';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { DataUser } from '../../../context/DataUser';
import { generateAudioFinal } from '../../../utils/audio/generateAudiofinal';
import { updateProject } from '../../../utils/project/CRUD';
import { deleteRecord } from '../../../utils/regions/deleteRecord';
import { ProgressProject } from '../../dashboard/ProgressProject';
import TrackingProject from '../../dashboard/TrackingProject';
import TabPanel, { a11yProps } from '../TabPanel/TabPanel';
import DownloadResources from './DownloadResources';
import FormDataProject from './FormDataProject';
import FormResourcesProject from './FormResourcesProject';
import FormRolesProject from './FormRolesProject';
import FormVoiceOver from './FormVoiceOver';
import styles from './ModalEditProject.module.css';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const ModalEditProject = ({ open, setOpen, project }) => {
  const { getProyects } = useContext(DataUser);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(1);
  const [formData, setFormData] = useState({ isDirty: false, data: {} });
  const [formDataRol, setFormDataRol] = useState({ isDirty: false, data: {} });
  const [updateData, setUpdateData] = useState(false);
  const [updateDataVoiceOver, setUpdateDataVoiceOver] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', type: 'error' });
  const [fileAudio, setFileAudio] = useState(null);
  const [guideline, setGuideline] = useState(null);
  const hasVoiceOver = project?.users?.some((user) => user?.role === 'AN_D'); 
  
  console.log('projectformodal', project);
  const handleChange = (event, newValue) => setValue(newValue);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setAlert({ open: false, message: '', type: 'error' });
  };

  const combineData = () => setUpdateData(true);
  const addService = () =>setUpdateDataVoiceOver(true)

  useEffect(() => {
    if (open) {
      // Comprueba si hay cambios en ambos estados antes de hacer la actualización
      if ((formData.data._id && formDataRol.data._id) && (formData.isDirty || formDataRol.isDirty)) {
        setLoading(true);
        const data = { ...formData.data, ...formDataRol.data }; // Combina ambos sets de datos
        if (data.name === project.name) delete data.name; // Si no se cambia el nombre, elimínalo
        delete data.__v;
        
        async function update(data) {
          await fetchUpdateProject(data); // Llama a la función que actualiza el proyecto en el backend
        }
        
        update(data); // Ejecuta la actualización
      } else if (formData.data._id && formDataRol.data._id) {
        // Si no hay cambios, resetea los estados y muestra alerta de que no hubo cambios
        setFormDataRol({ isDirty: false, data: {} });
        setFormData({ isDirty: false, data: {} });
        setAlert({ open: true, message: 'No changes were made', type: 'warning' });
        setUpdateData(false);
      }
    }
  }, [formData, formDataRol]);

  const fetchUpdateProject = async (data) => {
    try {
      await updateProject(data);
      setAlert({ open: true, message: 'Project updated successfully', type: 'success' });
      setLoading(false);
      await getProyects(1, true);
    } catch (err) {
      setAlert({ open: true, message: 'Error updating project', type: 'error' });
    } finally {
      setUpdateData(false);
    }
  };

  const uploadAudioProject = async (id, file) => {
    setLoading(true);

    if (!file && !guideline) {
      setAlert({ open: true, message: 'No file selected', type: 'warning' });
      setLoading(false);
      return;
    }

    if (file) {
      try {
        const format = file.name.split('.').pop().toLowerCase();

        // Paso 1 — Pedir URL presignada
        const presignedRes = await fetch(
          `${process.env.REACT_APP_URL}/api/uploads/presigned-url?projectId=${id}&bucket=audios&format=${format}`
        );
        const presignedData = await presignedRes.json();

        if (!presignedData.status) {
          setAlert({ open: true, message: `Error getting upload URL: ${presignedData.msg}`, type: 'error' });
          setLoading(false);
          return;
        }

        const { presignedUrl, contentType } = presignedData.data;

        // Paso 2 — Subir directo a S3 con seguimiento de progreso
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', presignedUrl);
          xhr.setRequestHeader('Content-Type', contentType);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`)));
          xhr.onerror = () => reject(new Error('S3 upload network error'));
          xhr.send(file);
        });

        // Paso 3 — Notificar al backend con la URL pública
        const publicUrl = presignedUrl.split('?')[0];
        const notifyRes = await fetch(`${process.env.REACT_APP_URL}/api/uploads/audio/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: publicUrl }),
        });
        const result = await notifyRes.json();

        if (result.status) {
          setAlert({ open: true, message: 'Audio uploaded successfully', type: 'success' });

          if (project.audio) {
            await updateProject({ _id: id, needExternalAudio: false });
            generateAudioFinal(id);
            setAlert({ open: true, message: 'AudioFinal started generating', type: 'success' });
          }

          if (guideline) {
            await updateProject({ _id: id, guideline });
          }

          setFileAudio(null);
          project.audio = publicUrl;
          setLoading(false);
          setProgress(0);
        } else {
          setAlert({ open: true, message: `Error uploading audio: ${result.msg}`, type: 'error' });
          setLoading(false);
          setProgress(0);
        }
      } catch (error) {
        console.error("Error uploading audio:", error);
        setAlert({ open: true, message: 'Error uploading audio', type: 'error' });
        setLoading(false);
        setProgress(0);
      }
    } else if (guideline && !file) {
      await updateProject({ _id: id, guideline }).then(async () => { await getProyects(1, true); }).catch((err) => { console.error(err); });
      setLoading(false);
    }
  }

  const handleCloseModal = async () => {
    setFormData({ isDirty: false, data: {} });
    setFormDataRol({ isDirty: false, data: {} });
    setOpen(false);
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Snackbar open={alert.open} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={alert.type} sx={{ width: '100%' }}>{alert.message}</Alert>
      </Snackbar>
      <Modal
        open={open}
        onClose={() => handleCloseModal()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.modal} >
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={value}
            onChange={handleChange}
            aria-label="Vertical tabs example"
            className={styles.tabs}
          >
            <Tab label={project?.name} className={styles.tab_title} sx={{
              '&.Mui-disabled': {
                color: 'var(--text-color)',
              },
            }} disabled />
            <Tab label="Edit Data Project" icon={<EditIcon sx={{ mr: 1 }} />} {...a11yProps(1)} />
            <Tab label="Edit Resources Project" icon={<EditIcon sx={{ mr: 1 }} />} {...a11yProps(2)} />
            <Tab label="Progres Project" icon={<AutoGraph sx={{ mr: 1 }} />} {...a11yProps(3)} />
            <Tab label={hasVoiceOver ? "Edit Voice Over" : "Add Voice Over"} icon={<EditIcon sx={{ mr: 1 }} />} {...a11yProps(4)} />
          </Tabs>
          <TabPanel value={value} index={0} />
          <TabPanel value={value} index={1}>
            <div className={styles.container_contetTab}>
              <div className={styles.header_tab}>
                <h2 className={styles.title_tab}>Edit Data Project</h2>
              </div>
              {loading ? (<h1>Loading...</h1>) : (
                <div className={styles.content_tab}>
                  <FormDataProject project={project} setFormData={setFormData} updateData={updateData} />
                  <FormRolesProject project={project} setFormDataRol={setFormDataRol} updateData={updateData} />
                </div>
              )}
              <div className={styles.footer_tab}>
                <Button variant="contained" color="secondary" onClick={() => handleCloseModal()}> Cancel </Button>
                <Button variant="contained" color="primary" disabled={loading} onClick={() => combineData()}> Update Data Project </Button>
              </div>
            </div>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <div className={styles.container_contetTab}>
              <div className={styles.header_tab}>
                <h2 className={styles.title_tab}>Edit Resources Project</h2>
              </div>
              <div className={styles.content_tab} >
                <FormResourcesProject project={project} style={{ width: '50%' }} setFileAudio={setFileAudio} progress={progress} loading={loading} setGuideline={setGuideline} />
                <DownloadResources project={project} setOpen={setOpen} />
              </div>
              <div className={styles.footer_tab}>
                <Button variant="contained" color="secondary" onClick={() => handleCloseModal()} > Cancel </Button>
                <Button variant="contained" color="primary" onClick={() => uploadAudioProject(project._id, fileAudio)} disabled={loading}> Update Data Project </Button>
              </div>
            </div>
          </TabPanel>
          <TabPanel value={value} index={3} style={{ height: ' calc(100% - 60px)' }}>
            <div className={styles.container_contetTab}>
              <div className={styles.header_tab}>
                <h2 className={styles.title_tab}>Tracking Project</h2>
              </div>
              <div className={styles.tracking_content}>
                <ProgressProject project={project} />
                <div
                  className={styles.tracking_content__right + ' ' + 'progressModal'} >
                  <TrackingProject project={project} />
                </div>
              </div>

            </div>
          </TabPanel>
          <TabPanel value={value} index={4} style={{ height: ' calc(100% - 60px)' }}>
            <div className={styles.container_contetTab}>
              <div className={styles.header_tab}>
                <h2 className={styles.title_tab}>{hasVoiceOver ? "Edit Voice Over" : "Add Voice Over"}</h2>
              </div>
              <div className={styles.content_tab} >
                <FormVoiceOver project={project} setAlert={setAlert} loading={loading} updateDataVoiceOver={updateDataVoiceOver} setLoading={setLoading} setUpdateDataVoiceOver={setUpdateDataVoiceOver} hasVoiceOver={hasVoiceOver}/>
              </div>
              <div className={styles.footer_tab}>
                <Button variant="contained" color="secondary" onClick={() => handleCloseModal()} > Cancel </Button>
                <Button variant="contained" color="primary" onClick={()=>addService()} disabled={loading}> {hasVoiceOver ? "Update Voice Over" : "Add Voice Over"} </Button>
              </div>
              </div>
              
          </TabPanel>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default ModalEditProject;